/**
 * Why a place picked for the signup Location field could not be saved as the account's address, as
 * a message for the field itself.
 *
 * The API accepts a city or finer (a US state passes too) but refuses a place that is only a
 * country: `PUT /address/v1.0/save` answers 400 with
 * `errors.address: ["Please provide street or city level full address"]`. Before this was
 * surfaced, that rejection escaped the submit and the form sat there with no message (ENG-514).
 */

/** The slice of i18next's `t` this needs: a key with its English fallback. */
type Translate = (key: string, defaultValue: string) => string

type AddressRejection = {
  response?: {
    status?: number
    data?: { errors?: { address?: unknown } }
  }
}

/**
 * True when the API refused the place for being too coarse to be an address (a country on its
 * own): a 400 that names the `address` field.
 */
export const isTooCoarseLocation = (error: unknown): boolean => {
  const response = (error as AddressRejection | null | undefined)?.response
  if (response?.status !== 400) return false
  const address = response.data?.errors?.address
  return Array.isArray(address) && address.length > 0
}

/** The message shown under the Location field when a picked place could not be saved. */
export const describeLocationSaveFailure = (error: unknown, t: Translate): string =>
  isTooCoarseLocation(error)
    ? t('constant.locationNeedsCity', 'Please choose a city or town. A country on its own is not enough.')
    : t('constant.locationSaveFailed', 'We could not use that location. Try again or pick another one.')
